import type {
  SellerInfo,
  SellerLoginParams,
  SellerLoginResponse,
} from "@/types/seller";
import { defineStore } from "pinia";
import { getSellerInfo, sellerLogin, sellerLogout } from "../api/user";
import { useCartStore } from "./cart";
/** Token Cookie 名称 */
const TOKEN_COOKIE_KEY = "B2BToken";

/** 用户信息 localStorage 名称 */
const USER_INFO_STORAGE_KEY = "B2BUserInfo";

/** 微信 openid 缓存 key（兼容历史缓存） */
const WECHAT_OPENID_STORAGE_KEYS = [
  "h5_wechat_openid",
  "wechat_openid",
  "openid",
];

/** 默认过期时间：7 天（秒） */
const DEFAULT_MAX_AGE = 365 * 24 * 60 * 60;

/**
 * 从 localStorage 获取用户信息
 */
function getUserInfoFromStorage(): SellerInfo | null {
  if (import.meta.server) return null;
  try {
    const stored = localStorage.getItem(USER_INFO_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * 保存用户信息到 localStorage
 */
function saveUserInfoToStorage(info: SellerInfo | null) {
  if (import.meta.server) return;
  try {
    if (info) {
      localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(info));
    } else {
      localStorage.removeItem(USER_INFO_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * 微信支付 openid 只在特定登录/授权接口返回，获取完整用户信息后需合并保留。
 */
function mergeWechatOpenid(info: SellerInfo, openid?: string) {
  const normalizedOpenid = openid?.trim();
  return normalizedOpenid ? { ...info, openid: normalizedOpenid } : info;
}

function clearWechatOpenidStorage() {
  if (import.meta.server) return;
  try {
    WECHAT_OPENID_STORAGE_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  } catch {
    // ignore storage errors
  }
}

export const useUserStore = defineStore("user", () => {
  /** Token Cookie（使用 useCookie 支持 SSR） */
  const tokenCookie = useCookie<string>(TOKEN_COOKIE_KEY, {
    maxAge: DEFAULT_MAX_AGE,
    path: "/",
  });

  /** 卖家信息（初始化时从 localStorage 恢复） */
  const userInfo = ref<SellerInfo | null>(getUserInfoFromStorage());

  /** 当前 Token */
  const token = computed(() => tokenCookie.value || "");

  /** 是否已登录 */
  const isLoggedIn = computed(() => !!tokenCookie.value);

  /** 手机号 */
  const mobile = computed(() => userInfo.value?.mobile || "");

  /** 卖家 ID */
  const sellerId = computed(() => userInfo.value?.id || 0);

  /**
   * 短信验证码登录
   * @param params 登录参数 { mobile, verify_code }，可选合并 localStorage 中的 promo_code
   */
  async function smsLogin(
    params: SellerLoginParams,
  ): Promise<SellerLoginResponse> {
    const payload: SellerLoginParams = { ...params };
    // 只有本 tab 是通过推广码进入的（sessionStorage 有标记），才带推广码
    if (import.meta.client && !payload.promo_code) {
      const sessionPromo = sessionStorage.getItem(PROMO_CODE_KEY)?.trim() || "";
      if (sessionPromo) {
        payload.promo_code = sessionPromo;
      }
    }

    const { data } = await sellerLogin(payload);
    const loginOpenid = data.openid?.trim();

    // 保存 Token 到 Cookie（响应式状态）
    tokenCookie.value = data.token;

    // 同步写入 document.cookie，确保 getToken() 能立即读到
    if (import.meta.client) {
      document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(data.token)};path=/;max-age=${DEFAULT_MAX_AGE}`;
    }

    // 登录成功后从后端拉取完整用户信息（包含 nickname、avatar 等）
    try {
      const info = await fetchUserInfo();
      if (loginOpenid) {
        const mergedInfo = mergeWechatOpenid(info, loginOpenid);
        userInfo.value = mergedInfo;
        saveUserInfoToStorage(mergedInfo);
      }
    } catch {
      // fetchUserInfo 失败时用登录接口返回的基础信息兜底
      const info: SellerInfo = {
        id: data.id,
        mobile: data.mobile,
        order_count: 0,
        create_at: data.create_at,
        update_at: data.create_at,
        last_used_at: Date.now(),
        available_compute_power: 0,
        openid: loginOpenid || undefined,
      };
      userInfo.value = info;
      saveUserInfoToStorage(info);
    }

    return data;
  }

  /**
   * 微信登录（授权码换 token 后调用）
   * @param loginData 后端返回的登录数据（含 token）
   */
  async function saveTokenAndFetchUser(
    loginData: SellerLoginResponse,
  ): Promise<SellerLoginResponse> {
    tokenCookie.value = loginData.token;
    const loginOpenid = loginData.openid?.trim();

    if (import.meta.client) {
      document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(loginData.token)};path=/;max-age=${DEFAULT_MAX_AGE}`;
    }

    try {
      const info = await fetchUserInfo();
      if (loginOpenid) {
        const mergedInfo = mergeWechatOpenid(info, loginOpenid);
        userInfo.value = mergedInfo;
        saveUserInfoToStorage(mergedInfo);
      }
    } catch {
      const info: SellerInfo = {
        id: loginData.id,
        mobile: loginData.mobile,
        order_count: 0,
        create_at: loginData.create_at,
        update_at: loginData.create_at,
        last_used_at: Date.now(),
        available_compute_power: 0,
        openid: loginOpenid || undefined,
      };
      userInfo.value = info;
      saveUserInfoToStorage(info);
    }

    return loginData;
  }

  /**
   * 退出登录
   */
  async function logout() {
    try {
      if (tokenCookie.value) {
        await sellerLogout();
      }
    } finally {
      clearUser();
      // 清空购物车数据
      const cartStore = useCartStore();
      cartStore.clearCart();
      // 清空订单统计数据
      const { useOrderStore } = await import("./order");
      const orderStore = useOrderStore();
      orderStore.clearOrderCounts();
    }
  }

  /**
   * 获取卖家信息
   */
  async function fetchUserInfo(): Promise<SellerInfo> {
    const { data } = await getSellerInfo();
    const mergedInfo = mergeWechatOpenid(data, userInfo.value?.openid);
    userInfo.value = mergedInfo;
    saveUserInfoToStorage(mergedInfo);
    return mergedInfo;
  }

  /**
   * 更新当前登录态中的微信 openid
   * @param openid 微信网页授权返回的 openid
   */
  function setWechatOpenid(openid: string) {
    const normalizedOpenid = openid.trim();
    if (!normalizedOpenid || !userInfo.value) return;

    const nextInfo = mergeWechatOpenid(userInfo.value, normalizedOpenid);
    userInfo.value = nextInfo;
    saveUserInfoToStorage(nextInfo);
  }

  /**
   * 清除用户状态
   */
  function clearUser() {
    tokenCookie.value = "";
    userInfo.value = null;
    saveUserInfoToStorage(null);
    clearWechatOpenidStorage();
  }

  return {
    // State
    userInfo,
    // Getters
    token,
    isLoggedIn,
    mobile,
    sellerId,
    // Actions
    smsLogin,
    saveTokenAndFetchUser,
    logout,
    fetchUserInfo,
    setWechatOpenid,
    clearUser,
  };
});
